import { type LDClient, type LDContext, type LDFlagSet, initialize } from "launchdarkly-js-client-sdk";
import { type User, captureException, captureMessage, setUser } from "@sentry/react";
import { client } from "api";
import { create } from "zustand";
import { createAuthRefresh } from "api/createAuthRefresh";
import { getCredentials } from "utils/trellisIntegration";
import { loginTrellis } from "api/login";
import configuration from "utils/config";
import type { CancelToken } from "axios";
import type { LoginResponse } from "api/responses";

/** The elements of authentication. */
export interface AuthProps {
    /** Indicates the status of the process of logging in. */
    authState: AuthState;

    /** Result of logging in to Payments. */
    loginResult?: LoginResponse;

    /** Function to trigger a re-login, which will refresh the authentication token. */
    doLogin: (cancelToken?: CancelToken) => Promise<LoginResponse | undefined>;

    /** Feature flags for the currently logged-in user. */
    featureFlags: LDFlagSet;
}

/** The potential authentication states of the system. */
export enum AuthState {
    /** No credentials were provided from the host application, authentication cannot proceed. */
    NoCredentials,
    /** Authentication is in progress. */
    InProgress,
    /** Authentication did not succeed. */
    Error,
    /** Authentication succeeded. */
    Success,
}

/** The credentials passed in by the host Vyne application. */
let credentials = getCredentials();
if (!credentials) {
    captureMessage("No credentials were provided by the host Vyne application.", "fatal");
}

let ldClient: LDClient | undefined;

/** Hook to track the state of filters for payment activity. */
export const useAuth = create<AuthProps>()((set, get) => ({
    authState: AuthState.InProgress,
    loginResult: undefined,
    featureFlags: {},
    /** log in and record the result. */
    doLogin: async (cancelToken?: CancelToken) => {
        const newResult = await doLoginInner(get().loginResult, cancelToken);

        // Update the default authorization header every time we get a new login result
        setApiAuth(newResult);

        set({
            authState: determineState(newResult),
            loginResult: newResult,
            featureFlags: ldClient?.allFlags() ?? ({} satisfies LDFlagSet),
        });

        return newResult;
    },
}));

// Set up the interceptor to refresh the auth token on 401s
createAuthRefresh();

async function doLoginInner(previousLoginResult: LoginResponse | undefined, cancelToken?: CancelToken) {
    // Slight optimization - don't bother making the request if we have no credentials, we know it'll fail.
    if (credentials) {
        const newResult = await loginTrellis(credentials, cancelToken);

        // Only update Sentry and LaunchDarkly if necessary, not on every refreshed auth token.
        if (newResult && hasUserChanged(newResult, previousLoginResult)) {
            setUser(buildSentryUser(newResult));

            await updateLDClient(newResult);
        }

        // If the login result includes a replacement Trellis JWT to use as a refresh token, update our stored
        // credentials to use that for subsequent logins.
        if (newResult?.trellisJWT) {
            credentials = {
                jwt: newResult.trellisJWT,
            };
        }

        return newResult;
    }
}

/**
 * Determine whether any of the user's information has changed after a login.
 * @param newResult The new login result (must not be undefined).
 * @param existingResult The previous login result (may be null or undefined).
 * @returns True if the user's details have changed, false otherwise.
 */
function hasUserChanged(newResult: LoginResponse, existingResult?: LoginResponse): boolean {
    // Can't just do shallow or deep comparison, we need to ignore the auth token and status flags that don't affect
    // the user's identity.
    return (
        !existingResult ||
        compareProps(existingResult, newResult, [
            "userId",
            "userName",
            "customerId",
            "trellisGlobalCustomerId",
            "customerName",
            "ldHash",
        ])
    );
}

/**
 * Determine if specified properties of two objects are equal.
 * @param first The first object to compare.
 * @param second The second object to compare.
 * @param props The specific properties on the two objects to compare.
 * @returns True if all the properties are the same on both objects, false otherwise.
 */
function compareProps<T>(first: T, second: T, props: (keyof T)[]) {
    return props.every((p) => first[p] === second[p]);
}

/**
 * Create or update the LaunchDarkly client after logging in.
 * @param newResult The successful login response from the server.
 */
async function updateLDClient(newResult: LoginResponse) {
    const context = buildLaunchDarklyUser(newResult);

    if (ldClient) {
        await ldClient.identify(context, newResult.ldHash);
    } else {
        try {
            const newLDClient = initialize(configuration.ldKey, context, { hash: newResult.ldHash });

            // wait 5 seconds for LaunchDarkly to initialize
            await newLDClient.waitForInitialization(5);
            ldClient = newLDClient;
        } catch (err) {
            captureException(err);
            ldClient = undefined;
        }
    }
}

/**
 * Build a Sentry user object from a login response.
 * @param result The result of logging in.
 * @returns The Sentry user object.
 */
function buildSentryUser(result: LoginResponse): User {
    return {
        id: result.userId.toString(),
        username: result.userName,
        customerId: result.customerId,
        customerName: result.customerName,
        trellisGlobalCustomerId: result.trellisGlobalCustomerId,
    };
}

/**
 * Build a LaunchDarkly user object from a login response.
 * This must match the logic in Lambda's FeatureFlagsService.BuildFromClaims.
 * @param result The result of logging in.
 * @returns The LaunchDarkly user object.
 */
function buildLaunchDarklyUser(result: LoginResponse): LDContext {
    return {
        kind: "user",
        key: result.trellisGlobalCustomerId.toString(),
        name: result.customerName,
        PaymentsCustomerId: result.customerId,
    };
}

/**
 * Determine the overall state of the authentication process.
 * @param loginResult Result of the last authentication attempt.
 * @returns Flag indicating overall state.
 */
function determineState(loginResult?: LoginResponse): AuthState {
    if (credentials) {
        if (loginResult) {
            return AuthState.Success;
        } else {
            return AuthState.Error;
        }
    } else {
        return AuthState.NoCredentials;
    }
}

/** Automatically authenticate API calls using token from a login response */
function setApiAuth(loginResult: LoginResponse | undefined) {
    const token = loginResult?.authToken;

    if (token) {
        client.defaults.headers.common = {
            Authorization: "Bearer " + token,
        };
    }
}
